import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, Skeleton, toast } from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';
import { formByIdQueryOptions, FormsFetchError } from '../forms-query';
import { FormMutationError, usePatchForm } from '../forms-mutations';
import { SurveyCreatorWrapper } from './SurveyCreatorWrapper';

/**
 * Form-edit page — wraps `survey-creator-react`. Loads the row, hydrates the
 * Creator from `definition`, lets the consultor build the survey, and posts
 * back via PATCH /forms/:id.
 *
 * The Creator is mounted via a small wrapper (`SurveyCreatorWrapper`) so the
 * heavy `survey-creator-react` import is colocated and can be lazy-loaded
 * later without changing this container.
 */
export default function FormEditPage() {
  const navigate = useNavigate();
  const { formId } = useParams({ from: '/_authenticated/formularios/$formId/editar' });
  // Workspace (área de trabalho) carried in the URL — preserved on navigation
  // back to the list so the guard doesn't bounce to the Home.
  const { companyId: workspaceCompanyId } = useSearch({
    from: '/_authenticated/formularios/$formId/editar',
  });

  const formQuery = useQuery(formByIdQueryOptions(formId));
  const patchForm = usePatchForm(formId);

  const [name, setName] = useState('');
  const [definition, setDefinition] = useState<unknown>(null);
  const [hasHydrated, setHasHydrated] = useState(false);

  // Hydrate local state once. We don't keep re-seeding on every refetch —
  // that would clobber unsaved local edits to the survey definition.
  useEffect(() => {
    if (hasHydrated || !formQuery.data) return;
    setName(formQuery.data.name);
    setDefinition(formQuery.data.definition ?? { pages: [] });
    setHasHydrated(true);
  }, [formQuery.data, hasHydrated]);

  // Bounce back to list on 404.
  useEffect(() => {
    if (!formQuery.isError) return;
    const status = formQuery.error instanceof FormsFetchError ? formQuery.error.status : 0;
    if (status === 404) {
      toast('Formulário não encontrado.');
      void navigate({ to: '/formularios', search: { companyId: workspaceCompanyId } });
    }
  }, [formQuery.isError, formQuery.error, navigate, workspaceCompanyId]);

  async function handleSave() {
    if (!hasHydrated) return;
    try {
      await patchForm.mutateAsync({
        name: name.trim(),
        definition,
      });
      toast('Formulário salvo.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
    } catch (err) {
      const status = err instanceof FormMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Formulário não encontrado.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar esse formulário.');
      } else {
        toast.error('Não foi possível salvar o formulário. Tente novamente.');
      }
    }
  }

  function handleBack() {
    void navigate({ to: '/formularios', search: { companyId: workspaceCompanyId } });
  }

  const initialDefinition = useMemo(
    () => formQuery.data?.definition ?? { pages: [] },
    [formQuery.data]
  );

  if (formQuery.isLoading || !hasHydrated) {
    return (
      <div className="flex min-h-dvh flex-row bg-muted">
        <ConsultantsMenu activeId="forms" />
        <main className="flex flex-1 flex-col bg-card">
          <div
            aria-busy="true"
            aria-label="Carregando formulário"
            className="flex flex-col gap-4 border-b border-border px-4 py-6 md:flex-row md:items-center md:justify-between md:px-8"
          >
            <div className="flex flex-col gap-2 md:flex-1">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-10 w-full max-w-md" />
            </div>
            <div className="flex flex-row gap-2">
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-28" />
            </div>
          </div>
          <div aria-hidden="true" className="flex flex-1 flex-col gap-4 px-4 py-6 md:px-8">
            <Skeleton className="h-8 w-full max-w-sm" />
            <Skeleton className="flex-1 min-h-96" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="forms" />

      <main className="flex flex-1 flex-col bg-card">
        <header className="flex flex-col gap-4 border-b border-border px-4 py-6 md:flex-row md:items-center md:justify-between md:px-8">
          <div className="flex flex-row items-center gap-3 md:flex-1">
            {/* Company logo — only for company-scoped forms; shared forms
                (companyId === null) carry no workspace, so no logo. */}
            {formQuery.data?.companyId ? (
              <WorkspaceBrand companyId={formQuery.data.companyId} />
            ) : null}
            <div className="flex flex-col gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Formulário
              </p>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome do formulário"
                className="max-w-[420px] text-lg font-medium"
                data-testid="form-edit-name"
              />
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <Button variant="outline" size="lg" onClick={handleBack}>
              Voltar
            </Button>
            <Button
              size="lg"
              onClick={() => void handleSave()}
              loading={patchForm.isPending}
              data-testid="form-edit-save"
            >
              Salvar
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-hidden">
          <SurveyCreatorWrapper
            formId={formId}
            initialDefinition={initialDefinition}
            onChange={setDefinition}
          />
        </div>
      </main>
    </div>
  );
}
